// src/lib/phoneUtils.js
// Phone number utilities for strict Philippines (+63) formatting

/**
 * Formats a phone number to strict +63 format
 * @param {string} input - Raw phone input
 * @returns {string} Formatted phone number in +63 format
 */
export function formatPhoneNumber(input) {
  if (!input) return '';
  
  // Remove all non-digits
  const digits = input.replace(/\D/g, '');
  
  // If starts with 63, remove it to get the 10 digits
  if (digits.startsWith('63')) {
    const remainingDigits = digits.slice(2);
    if (remainingDigits.length <= 10) {
      return `+63 ${remainingDigits}`;
    }
    // If more than 10 digits, truncate to 10
    return `+63 ${remainingDigits.slice(0, 10)}`;
  }
  
  // If starts with 0, remove it and format
  if (digits.startsWith('0')) {
    const remainingDigits = digits.slice(1);
    if (remainingDigits.length <= 10) {
      return `+63 ${remainingDigits}`;
    }
    // If more than 10 digits, truncate to 10
    return `+63 ${remainingDigits.slice(0, 10)}`;
  }
  
  // Otherwise, just format the digits (max 10)
  if (digits.length <= 10) {
    return `+63 ${digits}`;
  }
  return `+63 ${digits.slice(0, 10)}`;
}

/**
 * Formats a phone number with dashes for display
 * @param {string} input - Raw phone input
 * @returns {string} Formatted phone number with dashes
 */
export function formatPhoneNumberWithDashes(input) {
  if (!input) return '';
  
  // Remove all non-digits
  const digits = input.replace(/\D/g, '');
  
  // If starts with 63, remove it to get the 10 digits
  let phoneDigits = digits;
  if (digits.startsWith('63')) {
    phoneDigits = digits.slice(2);
  } else if (digits.startsWith('0')) {
    phoneDigits = digits.slice(1);
  }
  
  // Limit to 10 digits
  phoneDigits = phoneDigits.slice(0, 10);
  
  // Format as +63 XXX-XXX-XXXX
  if (phoneDigits.length >= 7) {
    return `+63 ${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
  } else if (phoneDigits.length >= 4) {
    return `+63 ${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3)}`;
  } else if (phoneDigits.length >= 1) {
    return `+63 ${phoneDigits}`;
  }
  
  return '+63';
}

/**
 * Validates if a phone number is in correct format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export function isValidPhoneNumber(phone) {
  if (!phone) return false;
  
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // Must be exactly 12 digits (+63 + 10 digits)
  if (digits.length !== 12) return false;
  
  // Must start with 63
  if (!digits.startsWith('63')) return false;
  
  // The remaining 10 digits should be valid mobile number
  const mobileDigits = digits.slice(2);
  
  // Philippine mobile numbers typically start with 9
  if (!mobileDigits.startsWith('9')) return false;
  
  return true;
}

/**
 * Normalizes a phone number to +63XXXXXXXXXX format (no spaces/dashes)
 * @param {string} input - Phone input
 * @returns {string} Normalized phone number
 */
export function normalizePhoneNumber(input) {
  if (!input) return '';
  
  // Remove all non-digits
  const digits = input.replace(/\D/g, '');
  
  // If starts with 63, keep it
  if (digits.startsWith('63')) {
    return `+${digits}`;
  }
  
  // If starts with 0, remove it and add +63
  if (digits.startsWith('0')) {
    return `+63${digits.slice(1)}`;
  }
  
  // Otherwise, add +63 prefix
  return `+63${digits}`;
}

/**
 * Handles phone input change with strict formatting
 * @param {string} value - Input value
 * @param {boolean} useDashes - Whether to use dashes in formatting
 * @returns {string} Formatted phone number
 */
export function handlePhoneInputChange(value, useDashes = false) {
  if (!value) return '+63';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // If starts with 63, remove it to get the 10 digits
  let phoneDigits = digits;
  if (digits.startsWith('63')) {
    phoneDigits = digits.slice(2);
  } else if (digits.startsWith('0')) {
    phoneDigits = digits.slice(1);
  }
  
  // Limit to 10 digits
  phoneDigits = phoneDigits.slice(0, 10);
  
  if (useDashes) {
    return formatPhoneNumberWithDashes(phoneDigits);
  }
  
  return formatPhoneNumber(phoneDigits);
}
